import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StatusBadge, MACHINE_STATUSES } from "@/components/Badges";
import { MapPin, Truck, HardHat, Search, FileDown, Columns2, Package, MinusCircle, AlertTriangle, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { API } from "@/lib/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfHeader, trAscii } from "@/lib/pdf";
import { toast } from "sonner";

const STATUS_TONE = {
  "Aktif Çalışıyor": "text-emerald-700",
  "Arızalı": "text-red-700",
  "Bakımda": "text-amber-700",
  "Boşta-Atıl": "text-zinc-600",
};

export default function Locations() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [machines, setMachines] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [sentMaterials, setSentMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [m, e, s] = await Promise.all([api.get("/machines"), api.get("/equipment"), api.get("/sent-materials")]);
        setMachines(m.data); setEquipment(e.data); setSentMaterials(s.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const reloadSent = async () => {
    try { const s = await api.get("/sent-materials"); setSentMaterials(s.data); } catch (e) {}
  };
  const [staleDays, setStaleDays] = useState(30);
  useEffect(() => { api.get("/settings").then((r) => setStaleDays(r.data.stale_days)).catch(() => {}); }, []);

  const [cLoc, setCLoc] = useState("all");
  const [cMonths, setCMonths] = useState(6);
  const [cData, setCData] = useState({ months: [], rows: [] });
  useEffect(() => {
    api.get("/reports/site-consumption", { params: { months: cMonths, location: cLoc === "all" ? undefined : cLoc } })
      .then((r) => setCData(r.data)).catch(() => {});
  }, [cLoc, cMonths]);
  const CHART_COLORS = ["#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444", "#EC4899", "#14B8A6", "#6366F1"];
  const cSeries = useMemo(() => {
    const seen = {};
    (cData.rows || []).forEach((r) => { if (!(r.part_name in seen)) seen[r.part_name] = r.unit; });
    return Object.entries(seen).map(([name, unit]) => ({ name, unit }));
  }, [cData]);
  const cChart = useMemo(() => (cData.months || []).map((ym) => {
    const o = { month: ym };
    (cData.rows || []).filter((r) => r.ym === ym).forEach((r) => { o[r.part_name] = (o[r.part_name] || 0) + r.quantity; });
    return o;
  }), [cData]);
  const exportConsumption = async () => {
    try {
      const params = new URLSearchParams({ months: String(cMonths) });
      if (cLoc !== "all") params.set("location", cLoc);
      const res = await fetch(`${API}/reports/site-consumption/export?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `aylik_saha_tuketim_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch (e) { toast.error("İndirme başarısız"); }
  };

  const locations = useMemo(() => {
    const set = new Set();
    machines.forEach((m) => m.location && set.add(m.location));
    equipment.forEach((e) => e.field && set.add(e.field));
    sentMaterials.forEach((s) => s.destination && set.add(s.destination));
    return Array.from(set).sort();
  }, [machines, equipment, sentMaterials]);

  const filteredMachines = machines.filter((m) =>
    (location === "all" || m.location === location) &&
    (statusFilter === "all" || m.status === statusFilter) &&
    [m.code, m.plate, m.name, m.operator].join(" ").toLowerCase().includes(q.toLowerCase())
  );
  const filteredEquipment = equipment.filter((e) =>
    (location === "all" || e.field === location) &&
    [e.name, e.serial_no, e.assigned_person].join(" ").toLowerCase().includes(q.toLowerCase())
  );
  const filteredSent = sentMaterials.filter((s) =>
    (location === "all" || s.destination === location) &&
    [s.part_name, s.part_code, s.destination, s.actor_name, s.note].join(" ").toLowerCase().includes(q.toLowerCase())
  );
  const netSummary = useMemo(() => {
    const map = {};
    filteredSent.forEach((s) => {
      const key = (location === "all" ? (s.destination || "") + "||" : "") + s.part_code;
      if (!map[key]) map[key] = { part_id: s.part_id, part_name: s.part_name, part_code: s.part_code, unit: s.unit, destination: s.destination, sent: 0, returned: 0, consumed: 0, lastDate: "" };
      const g = map[key];
      if (s.type === "İade") g.returned += s.quantity;
      else if (s.type === "Tüketim") g.consumed += s.quantity;
      else g.sent += s.quantity;
      const d = s.date || (s.timestamp || "").slice(0, 10);
      if (d > g.lastDate) g.lastDate = d;
      if (!g.part_id && s.part_id) g.part_id = s.part_id;
    });
    return Object.values(map).map((g) => {
      const net = g.sent - g.returned - g.consumed;
      const idle = g.lastDate ? Math.floor((Date.now() - new Date(g.lastDate).getTime()) / 86400000) : null;
      return { ...g, net, idle, stale: net > 0 && idle != null && idle >= staleDays };
    }).sort((a, b) => (b.stale - a.stale) || (b.lastDate > a.lastDate ? 1 : -1));
  }, [filteredSent, location, staleDays]);

  const statusCounts = MACHINE_STATUSES.map((s) => ({ s, n: filteredMachines.filter((m) => m.status === s).length }));

  const [exporting, setExporting] = useState(false);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeRow, setConsumeRow] = useState(null);
  const [cType, setCType] = useState("Tüketim");
  const [cQty, setCQty] = useState("");
  const [cDate, setCDate] = useState("");
  const [cNote, setCNote] = useState("");

  const openConsume = (g) => { setConsumeRow(g); setCType("Tüketim"); setCQty(""); setCDate(new Date().toISOString().slice(0, 10)); setCNote(""); setConsumeOpen(true); };
  const openReturn = (g) => { setConsumeRow(g); setCType("İade"); setCQty(""); setCDate(new Date().toISOString().slice(0, 10)); setCNote(""); setConsumeOpen(true); };
  const doConsume = async () => {
    const qn = parseFloat(cQty);
    if (isNaN(qn) || qn <= 0) { toast.error("Geçerli bir miktar girin"); return; }
    if (qn > consumeRow.net) { toast.error(`Bu sahada en fazla ${consumeRow.net} ${consumeRow.unit || ""} düşülebilir`); return; }
    try {
      await api.post(`/stock/${consumeRow.part_id}/movements`, { type: cType, quantity: qn, destination: consumeRow.destination, date: cDate, note: cNote });
      const msg = cType === "İade" ? "depoya iade edildi" : "sahada kullanıldı olarak düşüldü";
      toast.success(`${consumeRow.part_name}: ${qn} ${consumeRow.unit || ""} ${msg}`);
      setConsumeOpen(false); reloadSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const teslimTutanagi = (s) => {
    const t = trAscii;
    const doc = new jsPDF();
    let hy = pdfHeader(doc);
    doc.setFontSize(18); doc.setTextColor(0);
    doc.text(t("MALZEME TESLİM TUTANAĞI"), 105, hy + 8, { align: "center" });
    doc.setFontSize(11);
    let y = hy + 24;
    const row = (k, v) => { doc.setTextColor(110); doc.text(t(k), 20, y); doc.setTextColor(0); doc.text(t(String(v || "-")), 85, y); y += 10; };
    row("Malzeme", `${s.part_name || ""} (${s.part_code || "-"})`);
    row("Miktar", `${s.quantity} ${s.unit || ""}`);
    row("Gönderildiği Saha", s.destination);
    row("Teslim Tarihi", s.date || (s.timestamp || "").slice(0, 10));
    row("Teslim Eden (Depo)", s.actor_name);
    if (s.note) row("Açıklama", s.note);
    y += 8;
    doc.setDrawColor(180);
    doc.text(t("Teslim Eden"), 40, y + 30, { align: "center" });
    doc.line(20, y + 22, 90, y + 22);
    doc.text(t("Teslim Alan (Saha Şefi)"), 150, y + 30, { align: "center" });
    doc.line(120, y + 22, 190, y + 22);
    doc.setFontSize(9); doc.setTextColor(150);
    doc.text(t(`Bu tutanak ${new Date().toLocaleString("tr-TR")} tarihinde olusturulmustur.`), 105, 285, { align: "center" });
    doc.save(`malzeme_teslim_${(s.part_code || "malzeme")}_${(s.destination || "").replace(/\s+/g, "_")}.pdf`);
    toast.success("Teslim tutanağı indirildi");
  };

  const siteStats = (loc) => {
    const ms = machines.filter((m) => m.location === loc);
    const es = equipment.filter((e) => e.field === loc);
    const sm = sentMaterials.filter((s) => s.destination === loc);
    return {
      total: ms.length,
      equipment: es.length,
      equipmentInUse: es.reduce((a, e) => a + (e.in_use_qty || 0), 0),
      counts: MACHINE_STATUSES.map((s) => ({ s, n: ms.filter((m) => m.status === s).length })),
      reminders: ms.filter((m) => m.reminder_status === "soon" || m.reminder_status === "overdue").length,
      materials: sm.length,
    };
  };
  const exportReport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (location !== "all") params.set("location", location);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await fetch(`${API}/export/location?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saha_raporu_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch (e) {
      toast.error("İndirme başarısız");
    } finally { setExporting(false); }
  };

  return (
    <Layout
      title="Saha Sorgusu"
      subtitle="Bir sahayı seçin — orada hangi makineler ve ekipmanlar var, anında görün"
      actions={
        <Button data-testid="export-location-btn" onClick={exportReport} disabled={exporting} className="rounded-sm gap-2 bg-secondary hover:bg-secondary/90 text-white">
          <FileDown className="w-4 h-4" /> {exporting ? "Hazırlanıyor..." : "Excel'e Aktar"}
        </Button>
      }
    >
      <Tabs defaultValue="single" className="w-full">
        <TabsList className="rounded-sm mb-4 flex flex-wrap h-auto w-full sm:w-auto justify-start">
          <TabsTrigger data-testid="tab-single" value="single" className="rounded-sm gap-2"><MapPin className="w-4 h-4" /> Tekli Görünüm</TabsTrigger>
          <TabsTrigger data-testid="tab-compare" value="compare" className="rounded-sm gap-2"><Columns2 className="w-4 h-4" /> Saha Karşılaştırma</TabsTrigger>
          <TabsTrigger data-testid="tab-consumption" value="consumption" className="rounded-sm gap-2"><Package className="w-4 h-4" /> Aylık Tüketim</TabsTrigger>
        </TabsList>
        <TabsContent value="single" className="mt-0">
      <Card className="rounded-sm border border-border p-4 bg-white mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Saha / Lokasyon</label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger data-testid="location-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Sahalar</SelectItem>
                {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Makine Durumu</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="location-status-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {MACHINE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Ara</label>
            <div className="relative mt-1.5">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="location-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kod, plaka, personel..." className="pl-9 rounded-sm" />
            </div>
          </div>
        </div>
      </Card>

      {/* Status summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {statusCounts.map(({ s, n }) => (
          <Card key={s} className="rounded-sm border border-border p-4 bg-white">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{s}</p>
            <p className={`font-mono text-3xl font-bold mt-1 ${STATUS_TONE[s]}`}>{n}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Machines */}
        <Card className="rounded-sm border border-border overflow-hidden">
          <div className="px-4 py-3 bg-zinc-50 border-b border-border flex items-center gap-2">
            <Truck className="w-4 h-4 text-secondary" />
            <span className="font-display font-bold text-sm">
              {location === "all" ? "Tüm Sahalardaki Makineler" : `"${location}" Sahasındaki Makineler`} ({filteredMachines.length})
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-white">
                <TableHead className="text-xs font-bold uppercase tracking-wide">Kod / Plaka</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Makine</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Operatör</TableHead>
                {location === "all" && <TableHead className="text-xs font-bold uppercase tracking-wide">Lokasyon</TableHead>}
                <TableHead className="text-xs font-bold uppercase tracking-wide">Durum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
              {!loading && filteredMachines.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Bu sahada makine yok</TableCell></TableRow>}
              {filteredMachines.map((m) => (
                <TableRow key={m.id} data-testid={`loc-machine-${m.code}`} className="odd:bg-white even:bg-zinc-50/50">
                  <TableCell className="font-mono font-semibold">{m.code}<div className="text-xs text-muted-foreground font-normal">{m.plate || "-"}</div></TableCell>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell>{m.operator || "-"}</TableCell>
                  {location === "all" && <TableCell>{m.location}</TableCell>}
                  <TableCell><StatusBadge status={m.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Equipment */}
        <Card className="rounded-sm border border-border overflow-hidden">
          <div className="px-4 py-3 bg-zinc-50 border-b border-border flex items-center gap-2">
            <HardHat className="w-4 h-4 text-primary" />
            <span className="font-display font-bold text-sm">
              {location === "all" ? "Tüm Sahalardaki Ekipmanlar" : `"${location}" Sahasındaki Ekipmanlar`} ({filteredEquipment.length})
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-white">
                <TableHead className="text-xs font-bold uppercase tracking-wide">Ekipman</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Seri No</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Zimmetli Personel</TableHead>
                {location === "all" && <TableHead className="text-xs font-bold uppercase tracking-wide">Saha</TableHead>}
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Kullanımda</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
              {!loading && filteredEquipment.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Bu sahada ekipman yok</TableCell></TableRow>}
              {filteredEquipment.map((e) => (
                <TableRow key={e.id} data-testid={`loc-equipment-${e.serial_no || e.name}`} className="odd:bg-white even:bg-zinc-50/50">
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">{e.serial_no || "-"}</TableCell>
                  <TableCell>{e.assigned_person || "-"}</TableCell>
                  {location === "all" && <TableCell>{e.field || "-"}</TableCell>}
                  <TableCell className="text-right font-mono font-semibold text-amber-700">{e.in_use_qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Gönderilen Malzemeler & Net Durum */}
        <Card className="rounded-sm border border-border overflow-hidden" data-testid="sent-materials-card">
          <div className="px-4 py-3 bg-zinc-50 border-b border-border flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-700" />
            <span className="font-display font-bold text-sm">
              {location === "all" ? "Sahalara Gönderilen Malzemeler" : `"${location}" Sahasındaki Malzemeler`}
            </span>
          </div>

          {/* Net Durum (Sahada Kalan) */}
          <div className="px-4 pt-3">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Net Durum (Sahada Kalan)</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-white">
                <TableHead className="text-xs font-bold uppercase tracking-wide">Malzeme</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Kod</TableHead>
                {location === "all" && <TableHead className="text-xs font-bold uppercase tracking-wide">Saha</TableHead>}
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Gönderilen</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">İade</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Tüketilen</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Sahada Kalan</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Son Hareket</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
              {!loading && netSummary.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground">Bu sahaya gönderilen malzeme yok</TableCell></TableRow>}
              {netSummary.map((g, i) => (
                <TableRow key={i} data-testid={`net-material-${g.part_code}`} className={g.stale ? "bg-amber-50" : "odd:bg-white even:bg-zinc-50/50"}>
                  <TableCell className="font-medium">{g.part_name || "-"}</TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">{g.part_code || "-"}</TableCell>
                  {location === "all" && <TableCell>{g.destination}</TableCell>}
                  <TableCell className="text-right font-mono">{g.sent}</TableCell>
                  <TableCell className="text-right font-mono text-blue-700">{g.returned || 0}</TableCell>
                  <TableCell className="text-right font-mono text-amber-700">{g.consumed || 0}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-emerald-700">{g.net} {g.unit || ""}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {g.lastDate || "-"}
                    {g.stale && <span data-testid={`stale-badge-${g.part_code}`} className="ml-2 inline-flex items-center gap-1 rounded-sm bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-bold"><AlertTriangle className="w-3 h-3" />{g.idle} gündür bekliyor</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {g.net > 0 && g.part_id && canEdit && (
                      <div className="flex items-center justify-end gap-1">
                        <Button data-testid={`consume-btn-${g.part_code}`} onClick={() => openConsume(g)} variant="ghost" size="sm" className="rounded-sm gap-1 text-amber-700 h-8">
                          <MinusCircle className="w-3.5 h-3.5" /> Kullanıldı
                        </Button>
                        <Button data-testid={`return-btn-${g.part_code}`} onClick={() => openReturn(g)} variant="ghost" size="sm" className="rounded-sm gap-1 text-blue-700 h-8">
                          <MinusCircle className="w-3.5 h-3.5" /> İade
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Hareket Dökümü */}
          <div className="px-4 pt-4 border-t border-border">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Hareket Dökümü ({filteredSent.length})</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-white">
                <TableHead className="text-xs font-bold uppercase tracking-wide">Tarih</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Hareket</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Malzeme</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Miktar</TableHead>
                {location === "all" && <TableHead className="text-xs font-bold uppercase tracking-wide">Saha</TableHead>}
                <TableHead className="text-xs font-bold uppercase tracking-wide">Gönderen</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wide">Not</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Tutanak</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && filteredSent.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">Kayıt yok</TableCell></TableRow>}
              {filteredSent.map((s) => (
                <TableRow key={s.id} data-testid={`loc-material-${s.id}`} className="odd:bg-white even:bg-zinc-50/50">
                  <TableCell className="font-mono text-xs whitespace-nowrap">{s.date || (s.timestamp || "").slice(0, 10)}</TableCell>
                  <TableCell><span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-bold ${s.type === "İade" ? "bg-blue-100 text-blue-800" : s.type === "Tüketim" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{s.type || "Çıkış"}</span></TableCell>
                  <TableCell className="font-medium">{s.part_name || "-"} <span className="text-muted-foreground font-mono text-xs">{s.part_code}</span></TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${s.type === "İade" ? "text-blue-700" : s.type === "Tüketim" ? "text-amber-700" : "text-emerald-700"}`}>{s.type === "İade" || s.type === "Tüketim" ? "−" : "+"}{s.quantity} {s.unit || ""}</TableCell>
                  {location === "all" && <TableCell>{s.destination}</TableCell>}
                  <TableCell className="text-sm">{s.actor_name || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.note || "-"}</TableCell>
                  <TableCell className="text-right">
                    {(s.type === "Çıkış" || !s.type) && (
                      <Button data-testid={`teslim-pdf-${s.id}`} onClick={() => teslimTutanagi(s)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-secondary" title="Teslim Tutanağı (PDF)"><FileText className="w-4 h-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="compare" className="mt-0">
          <Card className="rounded-sm border border-border p-4 bg-white mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Saha A</label>
                <Select value={compareA} onValueChange={setCompareA}>
                  <SelectTrigger data-testid="compare-a-select" className="rounded-sm mt-1.5"><SelectValue placeholder="Saha seçin" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Saha B</label>
                <Select value={compareB} onValueChange={setCompareB}>
                  <SelectTrigger data-testid="compare-b-select" className="rounded-sm mt-1.5"><SelectValue placeholder="Saha seçin" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {(!compareA || !compareB) ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Karşılaştırmak için iki saha seçin.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[compareA, compareB].map((loc, idx) => {
                const st = siteStats(loc);
                return (
                  <Card key={idx} data-testid={`compare-col-${idx}`} className="rounded-sm border border-border overflow-hidden">
                    <div className="px-4 py-3 bg-secondary text-white flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="font-display font-bold text-sm">{loc}</span>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                          <p className="text-[10px] font-bold uppercase text-muted-foreground">Makine</p>
                          <p className="font-mono text-2xl font-bold">{st.total}</p>
                        </div>
                        <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                          <p className="text-[10px] font-bold uppercase text-muted-foreground">Ekipman</p>
                          <p className="font-mono text-2xl font-bold">{st.equipment}</p>
                        </div>
                        <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                          <p className="text-[10px] font-bold uppercase text-muted-foreground">Malzeme</p>
                          <p className="font-mono text-2xl font-bold text-emerald-700">{st.materials}</p>
                        </div>
                        <div className="bg-zinc-50 rounded-sm p-3 border border-border">
                          <p className="text-[10px] font-bold uppercase text-muted-foreground">Bakım Uyarısı</p>
                          <p className={`font-mono text-2xl font-bold ${st.reminders > 0 ? "text-red-700" : "text-zinc-900"}`}>{st.reminders}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Makine Durum Dağılımı</p>
                        {st.counts.map(({ s, n }) => (
                          <div key={s} className="flex items-center justify-between">
                            <StatusBadge status={s} />
                            <span className="font-mono font-bold">{n}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="consumption" className="mt-0">
          <Card className="rounded-sm border border-border p-6 bg-white" data-testid="consumption-report">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <h3 className="font-display font-bold text-lg">Aylık Saha Malzeme Tüketimi</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Sahalarda "Tüketim" olarak düşülen malzemelerin aylık trendi (malzeme bazında)</p>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Saha</Label>
                  <Select value={cLoc} onValueChange={setCLoc}>
                    <SelectTrigger data-testid="consumption-location" className="rounded-sm mt-1 w-48 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Sahalar</SelectItem>
                      {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Dönem</Label>
                  <Select value={String(cMonths)} onValueChange={(v) => setCMonths(parseInt(v, 10))}>
                    <SelectTrigger data-testid="consumption-months" className="rounded-sm mt-1 w-32 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">Son 3 ay</SelectItem>
                      <SelectItem value="6">Son 6 ay</SelectItem>
                      <SelectItem value="12">Son 12 ay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="consumption-export-btn" onClick={exportConsumption} variant="outline" className="rounded-sm gap-2 h-10"><FileDown className="w-4 h-4" /> Excel</Button>
              </div>
            </div>

            {cSeries.length === 0 ? (
              <p className="text-center py-16 text-muted-foreground">Seçilen dönemde tüketim kaydı yok</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={cChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E4E7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {cSeries.map((s, i) => (
                      <Bar key={s.name} dataKey={s.name} name={`${s.name} (${s.unit})`} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-6 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="text-xs font-bold uppercase tracking-wide">Malzeme</TableHead>
                        {(cData.months || []).map((m) => <TableHead key={m} className="text-right text-xs font-bold uppercase tracking-wide">{m}</TableHead>)}
                        <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Toplam</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cSeries.map((s) => {
                        const vals = (cData.months || []).map((m) => (cData.rows || []).filter((r) => r.part_name === s.name && r.ym === m).reduce((a, r) => a + r.quantity, 0));
                        const total = vals.reduce((a, b) => a + b, 0);
                        return (
                          <TableRow key={s.name} data-testid={`consumption-row-${s.name}`} className="odd:bg-white even:bg-zinc-50/50">
                            <TableCell className="font-medium">{s.name} <span className="text-muted-foreground text-xs">({s.unit})</span></TableCell>
                            {vals.map((v, i) => <TableCell key={i} className="text-right font-mono">{v || "-"}</TableCell>)}
                            <TableCell className="text-right font-mono font-bold text-amber-700">{total}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={consumeOpen} onOpenChange={setConsumeOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MinusCircle className={`w-5 h-5 ${cType === "İade" ? "text-blue-700" : "text-amber-700"}`} /> {cType === "İade" ? "Depoya İade (Düş)" : "Sahada Kullanıldı (Düş)"}
            </DialogTitle>
          </DialogHeader>
          {consumeRow && (
            <div className="py-2 space-y-3">
              <div className="bg-zinc-50 border border-border rounded-sm p-3 text-sm">
                <p className="font-semibold">{consumeRow.part_name} <span className="font-mono text-xs text-muted-foreground">({consumeRow.part_code})</span></p>
                <p className="text-muted-foreground mt-0.5">{consumeRow.destination} · Sahada kalan: <strong className="text-emerald-700 font-mono">{consumeRow.net} {consumeRow.unit}</strong></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{cType === "İade" ? "İade Miktarı *" : "Kullanılan Miktar *"}</Label>
                  <Input data-testid="consume-qty" type="number" value={cQty} onChange={(e) => setCQty(e.target.value)} className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Tarih</Label>
                  <DatePicker testid="consume-date" value={cDate} onChange={setCDate} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Not (opsiyonel)</Label>
                <Input data-testid="consume-note" value={cNote} onChange={(e) => setCNote(e.target.value)} placeholder="Örn. saha şefi bildirimi" className="rounded-sm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConsumeOpen(false)}>İptal</Button>
            <Button data-testid="consume-submit" className={`rounded-sm text-white ${cType === "İade" ? "bg-blue-600 hover:bg-blue-700" : "bg-amber-600 hover:bg-amber-700"}`} onClick={doConsume}>{cType === "İade" ? "Depoya İade Et" : "Sahadan Düş"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
